import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useClasses } from '@/features/classes';
import { useAtRisk } from '../queries';

/**
 * Teacher-dashboard counter: how many pupils of the first class the caller can
 * see are flagged, linking straight to that class's "Needs attention" panel.
 * One class, not all of them — the at-risk read is class-scoped by contract.
 */
export function AtRiskSummaryCard() {
  const { t } = useTranslation(['progress']);
  const classes = useClasses({ page_size: 1, is_archived: false });
  const firstClass = classes.data?.results[0];
  const atRisk = useAtRisk(firstClass?.public_id ?? '', { enabled: Boolean(firstClass) });

  const count = atRisk.data?.pupils.length ?? 0;
  const loading = classes.isLoading || (Boolean(firstClass) && atRisk.isLoading);

  if (!loading && !firstClass) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="text-muted-foreground size-4" aria-hidden />
          {t('progress:atRisk.dashboardTitle')}
        </CardTitle>
        <CardDescription>{t('progress:atRisk.dashboardDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <Skeleton className="h-9 w-full" aria-hidden />
        ) : count === 0 ? (
          <p className="text-muted-foreground text-sm">{t('progress:atRisk.dashboardEmpty')}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-medium tabular-nums">
              {t('progress:atRisk.count', { count })}
            </p>
            <span className="flex-1" />
            <Button variant="outline" size="sm" asChild>
              <Link to={`/classes/${firstClass?.public_id}`}>
                {t('progress:atRisk.dashboardOpen')}
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
